/**
 * The post editor.
 *
 * Three things here are structured pickers rather than free text, and the
 * handoff is specific about that — "an event select + an ambassador chip-picker
 * with search, not free text". A typed event name cannot be followed back to
 * the event, which is the whole point of linking them.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import type { TeamMember } from '@/features/auth/useTeamMembers'
import type { CrmEvent, Person } from '@/types/events'
import {
  CHANNELS,
  CHANNEL_COLORS,
  CONTENT_TYPES,
  POST_STATUSES,
  type Channel,
  type Post,
  type PostAsset,
} from '@/types/posts'
import './PublishingWorkspace.css'

interface Props {
  post: Post
  isNew: boolean
  events: CrmEvent[]
  people: Person[]
  partners: { id: string; name: string; kind: string }[]
  /** Who a post can be assigned to. Empty while it loads, and if it fails. */
  team: TeamMember[]
  /** The team member a new post starts on, by display name. '' for nobody. */
  defaultOwner: string
  onClose: () => void
  onSave: (draft: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

const newAsset = (kind: PostAsset['kind']): PostAsset => ({
  id: `a${Date.now()}`,
  kind,
  title: '',
  text: '',
  url: '',
})

export function PostModal({
  post,
  isNew,
  events,
  people,
  partners,
  team,
  defaultOwner,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const [form, setForm] = useState<Post>(post)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ambSearch, setAmbSearch] = useState('')
  const [ptnSearch, setPtnSearch] = useState('')

  const set = (patch: Partial<Post>) => setForm((f) => ({ ...f, ...patch }))

  /**
   * The default owner can arrive after the form does.
   *
   * The team is a live subscription, so a post opened in the first moments after
   * the screen loads would otherwise be created unassigned. Applied once and
   * only while nobody has touched the field, so it can never overwrite a
   * choice — including a deliberate Unassigned.
   */
  const ownerTouched = useRef(false)
  useEffect(() => {
    if (!isNew || ownerTouched.current || form.owner || !defaultOwner) return
    set({ owner: defaultOwner })
  }, [isNew, form.owner, defaultOwner])

  /**
   * The team, plus whoever this post already belongs to.
   *
   * An owner can be somebody who has left, or a name typed in while this was a
   * free-text field. Dropping them from the list would show the post as
   * unassigned and reassign it on the next unrelated save — so the stored name
   * stays an option until somebody deliberately picks another.
   */
  const ownerOptions = useMemo(() => {
    const names = team.map((m) => m.displayName)
    return form.owner && !names.includes(form.owner) ? [...names, form.owner] : names
  }, [team, form.owner])

  const toggleChannel = (c: Channel) =>
    set({
      channels: form.channels.includes(c)
        ? form.channels.filter((x) => x !== c)
        : // Kept in CHANNELS order, not click order, so the stored array reads
          // the same way the dot column renders it.
          CHANNELS.filter((x) => x === c || form.channels.includes(x)),
    })

  const toggleAmbassador = (id: string) =>
    set({
      linkedAmbassadorIds: form.linkedAmbassadorIds.includes(id)
        ? form.linkedAmbassadorIds.filter((x) => x !== id)
        : [...form.linkedAmbassadorIds, id],
    })

  const togglePartner = (id: string) =>
    setForm((f) => ({
      ...f,
      linkedPartnerIds: f.linkedPartnerIds.includes(id)
        ? f.linkedPartnerIds.filter((x) => x !== id)
        : [...f.linkedPartnerIds, id],
    }))

  const ptnMatches = useMemo(() => {
    const q = ptnSearch.trim().toLowerCase()
    // Unfiltered, the list is thirteen names — short enough to scan, so the
    // search is a convenience rather than the only way in.
    return q ? partners.filter((p) => p.name.toLowerCase().includes(q)) : partners
  }, [partners, ptnSearch])

  const ambMatches = useMemo(() => {
    const q = ambSearch.trim().toLowerCase()
    const list = q ? people.filter((p) => p.name.toLowerCase().includes(q)) : people
    return list.slice(0, 8)
  }, [people, ambSearch])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const { id: _id, createdAt: _c, updatedAt: _u, ...draft } = form
      void _id
      void _c
      void _u
      await onSave(draft, isNew ? undefined : post.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'New post' : `${form.type} · ${form.date || 'unscheduled'}`}
      onClose={onClose}
      width={860}
      footer={
        <>
          {!isNew && (
            <button
              className="btn btn--ghost pub__delete"
              disabled={saving}
              onClick={() => void onDelete(post.id)}
            >
              Delete post
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="postform">
        <div className="postform__grid">
          <label className="field">
            <span>Date</span>
            <input
              className="input"
              type="date"
              value={form.date}
              onChange={(e) => set({ date: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Time</span>
            <input
              className="input"
              type="time"
              value={form.time}
              onChange={(e) => set({ time: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Content type</span>
            <select
              className="input"
              value={form.type}
              onChange={(e) => set({ type: e.target.value as Post['type'] })}
            >
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select
              className="input"
              value={form.status}
              onChange={(e) => set({ status: e.target.value as Post['status'] })}
            >
              {POST_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Owner</span>
            <select
              className="input"
              value={form.owner}
              onChange={(e) => {
                ownerTouched.current = true
                set({ owner: e.target.value })
              }}
            >
              <option value="">Unassigned</option>
              {ownerOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Live post link</span>
            <input
              className="input"
              value={form.link}
              onChange={(e) => set({ link: e.target.value })}
              placeholder="https://…"
            />
          </label>
        </div>

        {/* What the post is for, from the 15 Aug round. These are what make an
            idea reviewable before anybody writes copy — an idea has no date and
            no channel, and without them there would be nothing on the record but
            a content type. */}
        <section className="postform__group">
          <h4 className="postform__legend">What it is for</h4>
          <div className="postform__grid">
            <label className="field postform__wide">
              <span>Subject</span>
              <input
                className="input"
                value={form.subject}
                placeholder="What it is about"
                onChange={(e) => set({ subject: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Goal</span>
              <input
                className="input"
                value={form.goal}
                placeholder="What it should achieve"
                onChange={(e) => set({ goal: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Target audience</span>
              <input
                className="input"
                value={form.targetAudience}
                placeholder="Who it is for"
                onChange={(e) => set({ targetAudience: e.target.value })}
              />
            </label>
          </div>
          {/* Two flags, not one choice: a post can serve both products, and a
              new one starts with neither rather than defaulting to a product
              nobody picked. */}
          <div className="postform__products">
            <label className="own__check">
              <input
                type="checkbox"
                checked={form.forPro}
                onChange={(e) => set({ forPro: e.target.checked })}
              />
              <span>Skida PRO</span>
            </label>
            <label className="own__check">
              <input
                type="checkbox"
                checked={form.forApp}
                onChange={(e) => set({ forApp: e.target.checked })}
              />
              <span>Skida.app</span>
            </label>
          </div>
        </section>

        <section className="postform__group">
          <h4 className="postform__legend">Channels (select one or more)</h4>
          <div className="postform__channels">
            {CHANNELS.map((c) => {
              const on = form.channels.includes(c)
              return (
                <button
                  key={c}
                  className={`chanchip${on ? ' chanchip--on' : ''}`}
                  onClick={() => toggleChannel(c)}
                  aria-pressed={on}
                  style={on ? { borderColor: CHANNEL_COLORS[c] } : undefined}
                >
                  <span className="chanchip__dot" style={{ background: CHANNEL_COLORS[c] }} />
                  {c}
                </button>
              )
            })}
          </div>
        </section>

        <section className="postform__group">
          <h4 className="postform__legend">Linked to</h4>
          <div className="postform__grid">
            <label className="field">
              <span>Event</span>
              <select
                className="input"
                value={form.linkedEventId}
                onChange={(e) => set({ linkedEventId: e.target.value })}
              >
                <option value="">— none —</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Find an ambassador</span>
              <input
                className="input"
                value={ambSearch}
                onChange={(e) => setAmbSearch(e.target.value)}
                placeholder="Type a name…"
              />
            </label>
          </div>

          {form.linkedAmbassadorIds.length > 0 && (
            <div className="postform__chips">
              {form.linkedAmbassadorIds.map((id) => {
                const p = people.find((x) => x.id === id)
                return (
                  <button key={id} className="ambchip" onClick={() => toggleAmbassador(id)}>
                    {p?.name ?? id} ✕
                  </button>
                )
              })}
            </div>
          )}

          <div className="postform__picklist">
            {ambMatches.map((p) => (
              <button
                key={p.id}
                className={`pickitem${form.linkedAmbassadorIds.includes(p.id) ? ' pickitem--on' : ''}`}
                onClick={() => toggleAmbassador(p.id)}
              >
                {p.name}
                <span className="muted"> · {p.homeArea || p.countries.join(', ')}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="postform__group">
          <h4 className="postform__legend">Linked partners (multiple allowed)</h4>
          <p className="postform__note">
            What this post was run for or with. It is how the plan answers “what did we do for
            this partner this season”, which is the question that comes up at renewal.
          </p>

          <div className="postform__row">
            <label className="field postform__wide">
              <span>Find a partner</span>
              <input
                className="input"
                value={ptnSearch}
                onChange={(e) => setPtnSearch(e.target.value)}
                placeholder="Type a name…"
              />
            </label>
          </div>

          {form.linkedPartnerIds.length > 0 && (
            <div className="postform__chips">
              {form.linkedPartnerIds.map((id) => {
                const p = partners.find((x) => x.id === id)
                return (
                  <button key={id} className="ambchip" onClick={() => togglePartner(id)}>
                    {p?.name ?? id} ✕
                  </button>
                )
              })}
            </div>
          )}

          <div className="postform__picklist">
            {ptnMatches.map((p) => (
              <button
                key={p.id}
                className={`pickitem${form.linkedPartnerIds.includes(p.id) ? ' pickitem--on' : ''}`}
                onClick={() => togglePartner(p.id)}
              >
                {p.name}
                <span className="muted"> · {p.kind}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="postform__group">
          <h4 className="postform__legend">Materials archive</h4>
          <p className="postform__note">
            Store the copy, images and video used for this post so it can be reused later. Images
            are held as a URL — this project has no file storage for post assets, and a drop slot
            that lost the file on reload would be worse than a field that says so.
          </p>

          {form.assets.map((a, i) => (
            <div className="asset" key={a.id}>
              <div className="asset__head">
                <input
                  className="input asset__title"
                  value={a.title}
                  placeholder={a.kind === 'text' ? 'Caption' : 'Title'}
                  onChange={(e) => {
                    const next = [...form.assets]
                    next[i] = { ...a, title: e.target.value }
                    set({ assets: next })
                  }}
                />
                <span className="asset__kind">{a.kind}</span>
                <button
                  className="btn-mini"
                  onClick={() => set({ assets: form.assets.filter((x) => x.id !== a.id) })}
                >
                  Remove
                </button>
              </div>
              {a.kind === 'text' ? (
                <textarea
                  className="input"
                  rows={3}
                  value={a.text}
                  onChange={(e) => {
                    const next = [...form.assets]
                    next[i] = { ...a, text: e.target.value }
                    set({ assets: next })
                  }}
                />
              ) : (
                <input
                  className="input"
                  value={a.url}
                  placeholder="https://…"
                  onChange={(e) => {
                    const next = [...form.assets]
                    next[i] = { ...a, url: e.target.value }
                    set({ assets: next })
                  }}
                />
              )}
            </div>
          ))}

          <div className="postform__addrow">
            {(['text', 'image', 'video'] as const).map((k) => (
              <button
                key={k}
                className="btn-mini"
                onClick={() => set({ assets: [...form.assets, newAsset(k)] })}
              >
                + {k}
              </button>
            ))}
          </div>
        </section>

        <label className="field">
          <span>Notes</span>
          <textarea
            className="input"
            rows={2}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </label>

        {error && <div className="postform__error">{error}</div>}
      </div>
    </Modal>
  )
}
